import { Column, Body1, convertHealthPlanEnumToName } from "@coherehealth/common";
import { User } from "@coherehealth/qm-api";
import { Box, IconButton } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import OverflowChipCell from "./OverflowChipCell";

// Chip colors per Figma design (node 8339:6832, measured from design context)
const skillsetChipStyle = { customBackgroundColor: "#e0f4fc", customLabelColor: "#027691" };
// Queue badges use the same magenta as the edit drawer's queue chips (Figma 8619:54232).
const queueChipStyle = { customBackgroundColor: "#faf0fa", customLabelColor: "#a002a0" };

// Collapsed row shows this many badges per column; anything beyond is an overflow "+N".
export const MAX_VISIBLE_SKILLS = 3;
export const MAX_VISIBLE_QUEUES = 2;
// The state-license column is plain text, so whether it truncates depends on the rendered width and
// can't be known from the data. Approximation: a row offers expand once it has more than this many
// states. Chosen from prod data (users export, 101 users with licenses: median 3, p75 6, p90 18 states)
// so the arrow stays off the ~70% of licensed users with 4 or fewer; revisit if column widths change.
export const MAX_VISIBLE_STATES = 4;

/** A row can expand only when at least one column has overflow to reveal. */
export function isUserRowExpandable(user: User): boolean {
  return (
    (user.skills?.length ?? 0) > MAX_VISIBLE_SKILLS ||
    (user.queues?.length ?? 0) > MAX_VISIBLE_QUEUES ||
    (user.licensedStates?.length ?? 0) > MAX_VISIBLE_STATES
  );
}

interface ColumnOptions {
  onEditUser: (user: User) => void;
  onToggleExpand: (user: User) => void;
  expandedUserIds: Set<string>;
}

export function getUserTableColumns({ onEditUser, onToggleExpand, expandedUserIds }: ColumnOptions): Column<User>[] {
  return [
    {
      name: "name",
      label: "Name",
      width: "15%",
      themedPaddingRight: 1,
      value: (user: User) => (
        <Body1 noWrap style={{ maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" }}>
          {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.name || "--"}
        </Body1>
      ),
    },
    {
      name: "skills",
      label: "Skillsets",
      width: "27%",
      themedPaddingRight: 1,
      unSortable: true,
      value: (user: User) => {
        const isExpanded = expandedUserIds.has(user.id ?? "");
        return (
          <OverflowChipCell
            items={user.skills ?? []}
            maxVisible={isExpanded ? Infinity : MAX_VISIBLE_SKILLS}
            chipStyle={skillsetChipStyle}
          />
        );
      },
    },
    {
      name: "queues",
      label: "Assigned queues",
      width: "27%",
      themedPaddingRight: 1,
      unSortable: true,
      value: (user: User) => {
        const isExpanded = expandedUserIds.has(user.id ?? "");
        return (
          <OverflowChipCell
            items={user.queues ?? []}
            maxVisible={isExpanded ? Infinity : MAX_VISIBLE_QUEUES}
            chipStyle={queueChipStyle}
          />
        );
      },
    },
    {
      name: "opsGroup",
      label: "Ops group",
      width: "12%",
      themedPaddingRight: 1,
      value: (user: User) => convertHealthPlanEnumToName(user.opsGroup) || "--",
    },
    {
      name: "licensedStates",
      label: "State licenses",
      width: "9%",
      themedPaddingRight: 1,
      // Collapsed rows truncate with an ellipsis rather than wrapping onto a second line (full list on
      // hover); expanded rows wrap to show every state.
      value: (user: User) => {
        const states = user.licensedStates?.length ? user.licensedStates.join(", ") : "--";
        const isExpanded = expandedUserIds.has(user.id ?? "");
        return (
          <Body1
            noWrap={!isExpanded}
            title={states}
            style={
              isExpanded
                ? { maxWidth: "100%", whiteSpace: "normal", overflowWrap: "anywhere" }
                : { maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis" }
            }
          >
            {states}
          </Body1>
        );
      },
    },
    {
      name: "actions",
      label: " ",
      width: "60px",
      themedPaddingRight: 0,
      unSortable: true,
      value: (user: User) => {
        const isExpanded = expandedUserIds.has(user.id ?? "");
        const expandable = isUserRowExpandable(user);
        return (
          <Box
            className={isExpanded ? "" : "actions-hover"}
            sx={{
              position: "absolute",
              right: "24px",
              top: "50%",
              transform: "translateY(-50%)",
              display: "flex",
              gap: "4px",
              alignItems: "center",
            }}
          >
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEditUser(user);
              }}
              aria-label="Edit user"
              data-testid={`edit-user-${user.id}`}
              sx={{ width: 20, height: 20, padding: 0 }}
            >
              <EditIcon sx={{ fontSize: 20 }} />
            </IconButton>
            {expandable && (
              <IconButton
                className="expandable-row"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpand(user);
                }}
                aria-label={isExpanded ? "Collapse" : "Expand"}
                data-testid={`expand-user-${user.id}`}
                sx={{
                  width: 16,
                  height: 16,
                  padding: 0,
                  transform: isExpanded ? "rotate(180deg)" : "none",
                  transition: "transform 0.2s",
                }}
              >
                <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
          </Box>
        );
      },
    },
  ];
}
